import { EQueryType } from '@opengov/reporting-types';

export interface IQueryState extends IBaseQueryRequestParams {
  id: string;
  queryType?: EQueryType;
  result?: IOgqlJobResult;
  maxPollAttempts?: number;
}

export interface IOgqlPollParams extends IQueryState {
  pollAttempts: number;
  pollDelay: number;
}

export interface IBaseQueryRequestParams {
  id?: string;
  queryType?: EQueryType;
  version?: string;
  queryString: string;
  queryParams?: {
    dimensionalSummaryQueryIndex?: number;
    childParentKey?: string;
    allowQueryExecution?: boolean;
  };
  fastReturn?: boolean;
  useQueryService?: boolean;
  viewId?: string;
  lensId?: string;
}

export interface IViewQueryRequestParams extends IBaseQueryRequestParams {
  viewId: string;
  lensId?: never;
}

export interface ILensQueryRequestParams extends IBaseQueryRequestParams {
  viewId?: never;
  lensId: string;
}

export type IQueryRequestParams = IViewQueryRequestParams | ILensQueryRequestParams;

export interface IOgqlSemanticType {
  type: string; //tslint:disable-line:no-reserved-keywords
  name: string;
  units?: string;
}

export interface IOgqlColumn {
  name: string;
  type: string; //tslint:disable-line:no-reserved-keywords
  metadata: {
    tags: {
      hidden?: boolean;
      units?: string;
    };
    facts: {
      distinctCount?: string; // OGQL returns strings even for numeric values
      minimum: string;
      maximum: string;
    };
    units?: string;
  };
  displayName: string;
  semanticTypes: IOgqlSemanticType[];
}

export interface IOgqlView {
  id: string;
  version: string;
  visible: boolean;
  name: string;
  entityId: number;
  source: string;
  dataUpdatedAt: string;
}

export interface IOgqlViewCapabilities {
  export: {
    exportable: boolean;
    maxExportRows: number;
  };
  groupBy: string[];
  orderBy: string[];
}

export interface IOgqlViewDetail extends IOgqlView {
  columns?: IOgqlColumn[];
  capabilities?: IOgqlViewCapabilities;
}

export interface IOgqlQueryResponse {
  id: string;
  jobState: EOgqlJobState.created | EOgqlJobState.enqueued | EOgqlJobState.finished;
}

export interface IOgqlQueryError {
  code: number;
  message: string;
  type: string; //tslint:disable-line:no-reserved-keywords
}

export enum EOgqlJobState {
  created = 'created',
  enqueued = 'enqueued',
  finished = 'finished',
  failed = 'failed',
  running = 'running',
  cancelled = 'cancelled'
  // Cancelled is not currently supported by the OGQL v0 API itself but, the query effects will return this
  // value when the query is cancelled.
}

export interface IOgqlV1Column {
  label: string;
  logicalType: string;
  name: string;
  semanticTypes: string[];
  visible: boolean;
}

export interface IOgqlV1QueryResult {
  data: any[][];
  query: string;
  metadata: {
    columns: IOgqlV1Column[];
  };
  version: string;
  view_id: string;
  pagination?: {
    next?: {
      query: string;
      version: string;
      viewId: string;
    };
    prev?: {
      query: string;
      version: string;
      viewId: string;
    };
  };
}

export interface IOgqlQueryResult {
  data: any[][];
  query: string;
  columns: IOgqlColumn[];
  version: string;
  viewId: string;
  pagination?: {
    next?: {
      query: string;
      version: string;
      viewId: string;
    };
    prev?: {
      query: string;
      version: string;
      viewId: string;
    };
  };
}

export interface IOgqlJobResult {
  id: string;

  // jobState is used for the job returned for the job creation
  // state is used when polled
  state?: EOgqlJobState;
  jobState?: EOgqlJobState;
  timestamps?: {
    createdAt: string;
    startedAt: string;
    finishedAt: string;
  };
  jobResult?: IOgqlQueryResult;
  errors?: IOgqlQueryError[];
}

export interface IOgqlV1JobResult {
  id: string;

  // jobState is used for the job returned for the job creation
  // state is used when polled
  state?: EOgqlJobState;
  jobState?: EOgqlJobState;
  timestamps?: {
    createdAt: string;
    startedAt: string;
    finishedAt: string;
  };
  jobResult?: IOgqlV1QueryResult;
  errors?: IOgqlQueryError[];
}

export interface IOgqlViewListResult {
  views: IOgqlView[];
  meta: {
    total: number;
    next: {
      limit: number;
      offset: number;
    };
  };
}

export type IOgqlV1DataAndColumns = {
  data: IOgqlV1JobResult['jobResult']['data'];
  columns: IOgqlV1JobResult['jobResult']['metadata']['columns'];
};

export const magicNotApplicableString = '-2147483647.0';
export const magicNotApplicableNumber = -2147483647;
